import { useState } from 'react';

import { Button, Flex, Grid, Modal, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';
import { OnChangeValue } from 'react-select';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { billingApiSlice } from '@/store/slices/api';
import { useClearAllDiagnosesMutation } from '@/store/slices/api/patientDiagnosesApiSlice';
import {
  patientsApiSlice,
  useAddDiagnosisCodeMutation,
  useDeleteDiagnosisCodeMutation,
  useGetPatientDiagnosesInfiniteQuery
} from '@/store/slices/api/patientsApiSlice';
import {
  transmissionReportApiSlice,
  useAddTransmissionReportDiagnosisCodeMutation,
  useDeleteTransmissionReportDiagnosisCodeMutation
} from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { API_SLICE } from '@/utils/constants/apiEndpoints';

import DiagnosisSelect, {
  LabelAndValue
} from '@/pages/physician/ReviewTransmissionReportsPage/TransmissionReportDetails/Inputs/DiagnosisSelect';
import { PatientDiagnosis } from '@/types/patient';

import DiagnosisCodesTable from './DiagnosisCodesTable';
import styles from './DxCodeSelectionModal.module.scss';

type ConfirmationType = 'add' | 'clear-all' | null;

const LIST_LIMIT = 20;

const DxCodeSelectionModal = ({
  opened,
  onClose,
  onSave,
  patientId,
  reportId,
  onChange
}: {
  opened: boolean;
  onClose: () => void;
  onSave: (diagnosisCode: LabelAndValue | null, diagnosisId?: string) => Promise<void>;
  patientId: string;
  reportId?: number;
  onChange: (diagnosis: LabelAndValue | null) => void;
}) => {
  const [selectedDiagnosis, setSelectedDiagnosis] = useState<LabelAndValue | null>(null);
  const [confirmationType, setConfirmationType] = useState<ConfirmationType | null>(null);
  const dispatch = useAppDispatch();

  const { isActiveReportFinalSigned } = useAppSelector((state) => state.transmissionReportForm);

  const [addDiagnosisCode, { isLoading: isAddingDiagnosis }] = useAddDiagnosisCodeMutation();
  const [deleteDiagnosisCode, { isLoading: isDeleting }] = useDeleteDiagnosisCodeMutation();
  const [clearAllDiagnoses, { isLoading: isClearingAll }] = useClearAllDiagnosesMutation();
  const [addTransmissionReportDiagnosisCode, { isLoading: isAddingTransmissionDiagnosis }] =
    useAddTransmissionReportDiagnosisCodeMutation();
  const [deleteTransmissionReportDiagnosisCode, { isLoading: isDeletingTransmissionDiagnosis }] =
    useDeleteTransmissionReportDiagnosisCodeMutation();

  const { data: patientDiagnoses } = useGetPatientDiagnosesInfiniteQuery(
    {
      patientId,
      limit: LIST_LIMIT
    },
    {
      skip: isActiveReportFinalSigned
    }
  );

  const handleDiagnosisChange = (value: OnChangeValue<LabelAndValue, false>) => {
    if (value) {
      setSelectedDiagnosis(value);
      setConfirmationType('add');
    }
  };

  const handleConfirmAdd = async () => {
    if (selectedDiagnosis) {
      try {
        if (isActiveReportFinalSigned && reportId) {
          await addTransmissionReportDiagnosisCode({
            reportId,
            diagnosisCode: selectedDiagnosis.value
          }).unwrap();
        } else {
          await addDiagnosisCode({
            diagnosisCode: selectedDiagnosis.value,
            patientId
          }).unwrap();
        }

        dispatch(billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: reportId }]));

        setSelectedDiagnosis(null);
        setConfirmationType(null);

        if (patientDiagnoses?.pages[0]?.total === 0) {
          onChange({
            label: `${selectedDiagnosis.label} - ${selectedDiagnosis.value}`,
            value: selectedDiagnosis.value
          });

          dispatch(
            transmissionReportApiSlice.util.invalidateTags([
              { type: API_SLICE.TransmissionReport, id: reportId }
            ])
          );
        }

        notifications.show({
          title: 'Condition Added',
          message: `Successfully added ${selectedDiagnosis.value} to ${isActiveReportFinalSigned ? 'report' : "patient's conditions"}.`,
          color: 'green'
        });
      } catch (error: any) {
        if (error.status === 409) {
          notifications.show({
            title: 'Duplicate Condition',
            message: `${selectedDiagnosis.value} is already in the ${isActiveReportFinalSigned ? 'report' : "patient's condition list"}.`,
            color: 'red'
          });
        } else {
          notifications.show({
            title: 'Add Condition Failed',
            message: 'Unable to add the condition. Please try again.',
            color: 'red'
          });
        }
        console.error('Failed to add diagnosis:', error);
      }
    }
  };

  const handleCancelAdd = () => {
    setSelectedDiagnosis(null);
    setConfirmationType(null);
  };

  const handleClearAllConditions = () => {
    setConfirmationType('clear-all');
  };

  const handleConfirmClearAll = async () => {
    try {
      await clearAllDiagnoses({ patientId }).unwrap();

      dispatch(
        patientsApiSlice.util.invalidateTags([
          { type: API_SLICE.Patient, id: `${patientId}-diagnoses` }
        ])
      );

      dispatch(billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: reportId }]));

      dispatch(
        transmissionReportApiSlice.util.invalidateTags([
          { type: API_SLICE.TransmissionReport, id: reportId }
        ])
      );

      setConfirmationType(null);
      onChange(null);

      notifications.show({
        title: 'All Conditions Cleared',
        message: 'All conditions have been successfully removed from the patient.',
        color: 'green'
      });
    } catch (error) {
      notifications.show({
        title: 'Clear All Conditions Failed',
        message: 'Unable to clear all conditions. Please try again.',
        color: 'red'
      });
      console.error('Failed to clear all diagnosis codes:', error);
    }
  };

  const handleCancelClearAll = () => {
    setConfirmationType(null);
  };

  const handleMakePrimary = async (diagnosis: PatientDiagnosis) => {
    if (diagnosis) {
      try {
        await onSave(
          {
            label: `${diagnosis.diagnosisCode} - ${diagnosis.description}`,
            value: diagnosis.diagnosisCode
          },
          diagnosis.id
        );

        dispatch(
          patientsApiSlice.util.invalidateTags([
            { type: API_SLICE.Patient, id: `${patientId}-diagnoses` }
          ])
        );
        dispatch(
          transmissionReportApiSlice.util.invalidateTags([
            { type: API_SLICE.TransmissionReport, id: reportId }
          ])
        );

        notifications.show({
          title: 'Primary Condition Set',
          message: `${diagnosis.diagnosisCode} is now the primary condition.`,
          color: 'green'
        });
      } catch (error) {
        notifications.show({
          title: 'Set Primary Failed',
          message: 'Unable to set the primary condition. Please try again.',
          color: 'red'
        });
        console.error('Failed to make primary diagnosis:', error);
      }
    }
  };

  const handleDeleteDiagnosis = async (diagnosisId: string, diagnosisCode?: string) => {
    try {
      if (isActiveReportFinalSigned && reportId && diagnosisCode) {
        await deleteTransmissionReportDiagnosisCode({ reportId, diagnosisCode }).unwrap();
      } else {
        await deleteDiagnosisCode({ diagnosisId, patientId }).unwrap();
      }

      dispatch(billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: reportId }]));

      notifications.show({
        title: 'Condition Removed',
        message: `The condition has been successfully removed from the ${isActiveReportFinalSigned ? 'report' : 'patient'}.`,
        color: 'green'
      });
    } catch (error) {
      notifications.show({
        title: 'Remove Condition Failed',
        message: 'Unable to remove the condition. Please try again.',
        color: 'red'
      });
      console.error('Failed to delete diagnosis:', error);
    }
  };

  const renderConfirmationSection = () => {
    switch (confirmationType) {
      case 'add':
        return (
          <div className={styles.confirmationSection}>
            <Text size="sm" fw={500} c="plum">
              Are you sure you want to add this condition?
            </Text>
            <Text size="sm" mb="sm" c="plum">
              {selectedDiagnosis?.label}
            </Text>
            <Flex gap="sm">
              <Button
                size="xs"
                onClick={handleConfirmAdd}
                loading={isAddingDiagnosis || isAddingTransmissionDiagnosis}
                className={styles.modalButton}>
                Add Condition
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={handleCancelAdd}
                className={styles.modalButton}>
                Cancel
              </Button>
            </Flex>
          </div>
        );
      case 'clear-all':
        return (
          <div className={styles.confirmationSection}>
            <Text size="sm" fw={500} c="plum">
              Are you sure you want to clear all conditions?
            </Text>
            <Text size="sm" mb="sm" c="plum">
              This action cannot be undone.
            </Text>
            <Flex gap="sm">
              <Button
                size="xs"
                onClick={handleConfirmClearAll}
                loading={isClearingAll}
                className={styles.modalButton}>
                Clear All
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={handleCancelClearAll}
                className={styles.modalButton}>
                Cancel
              </Button>
            </Flex>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      closeOnClickOutside={false}
      withCloseButton={true}
      title={
        <Text fw={600} size="lg" c="plum.9">
          Add Conditions
        </Text>
      }
      centered
      radius="md"
      size={'xl'}
      classNames={{
        header: styles.dxCodeSelectionModalHeader,
        body: styles.dxCodeSelectionModalBody
      }}>
      <Grid columns={12}>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Text size="sm" c="plum.9" fw={600} pb={4}>
            Add New Condition
          </Text>
          <DiagnosisSelect
            name={'diagnosisOption'}
            placeholder="Search by ICD-10 Code or Description"
            category={'patient'}
            value={null}
            defaultValue={undefined}
            onChange={handleDiagnosisChange}
            isLoading={false}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>{renderConfirmationSection()}</Grid.Col>
      </Grid>
      <Flex direction="column" gap={4} mt="md">
        <Flex gap={'sm'} align="center">
          <Text size="sm" c="plum.9" fw={600}>
            Active Conditions
          </Text>
          {!isActiveReportFinalSigned && (
            <Button
              size="xs"
              variant="outline"
              onClick={handleClearAllConditions}
              c="plum"
              px={8}
              h={24}
              loading={isClearingAll}>
              <IconTrash size={14} />
              <Text size="xs" ml={4} c="plum" fw={500}>
                Clear All Conditions
              </Text>
            </Button>
          )}
        </Flex>

        <DiagnosisCodesTable
          patientId={patientId}
          reportId={reportId}
          onMakePrimary={handleMakePrimary}
          onDelete={(diagnosisId, diagnosisCode) =>
            handleDeleteDiagnosis(diagnosisId, diagnosisCode)
          }
          isLoading={
            isAddingDiagnosis ||
            isDeleting ||
            isClearingAll ||
            isAddingTransmissionDiagnosis ||
            isDeletingTransmissionDiagnosis
          }
        />
      </Flex>
    </Modal>
  );
};

export default DxCodeSelectionModal;
